import React, { useEffect, useState } from "react";
import { Button, Stack, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import UploadBox from "./UploadBox";
import { AbstractCase } from "../../../types/Entities/AbstractCase";
import CheckIcon from "../../../assets/icons/common/toggleable/IconCheck";
import CrossIcon from "../../../assets/icons/common/toggleable/IconClose";
import Pad from "../../Wizard/Pad";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createResource, queryKeys } from "../../../services/reactQuery/reactQueryService";
import _ from "lodash";
import { getFinishedStep } from "../../../services/messagingService";
import { MessageCreateData } from "../../../types/Messaging/MessageCreateData";
import structuredClone from "@ungap/structured-clone";
import { MediaObjectCombinationRequest } from "../../../types/Entities/MediaObject/MediaObjectCombinationRequest";
import ProductBox from "@/src/components/ProductBox/ProductBox";

const FURTHER_DOCUMENT_LIMIT = 9;

type AttachmentFormStepProps = {
  product: AbstractCase;
  currentStep: number;
  setCurrentStep: Function;
  steps: number;
  setSteps: Function;
  setIsLoading: Function;
  headline: string;
  subHeadline: string;
  messageCreateData: MessageCreateData | null;
  mediaObjectCombinationRequestUpdateAction: (id: number, data: Record<string, any>) => Promise<void>;
  mediaObjectCombinationRequest: MediaObjectCombinationRequest;
};

export default function AttachmentFormStep({
  product,
  currentStep,
  setCurrentStep,
  steps,
  setSteps,
  setIsLoading,
  headline,
  messageCreateData,
  subHeadline,
  mediaObjectCombinationRequestUpdateAction,
  mediaObjectCombinationRequest,
}: AttachmentFormStepProps) {
  const [hasFurtherDocument, setHasFurtherDocument] = useState(false);
  const [description, setDescription] = useState("");
  const queryClient = useQueryClient();
  const createMutation = useMutation({
    mutationFn: createResource,
  });

  //since the first step isnt a file upload
  const filesToCombineDataCurrentFileIndex = currentStep - 1;

  useEffect(() => {
    let mediaObjectCombinationRequestClone = structuredClone(mediaObjectCombinationRequest);
    if (hasFurtherDocument && !mediaObjectCombinationRequestClone.filesToCombine[filesToCombineDataCurrentFileIndex]) {
      _.set(mediaObjectCombinationRequestClone, "filesToCombine[" + filesToCombineDataCurrentFileIndex + "]", {
        files: [],
        description: "",
        fieldName: "uploadedFile",
      });
      mediaObjectCombinationRequestUpdateAction(mediaObjectCombinationRequest.id, mediaObjectCombinationRequestClone);
    }
    if (!hasFurtherDocument && mediaObjectCombinationRequestClone.filesToCombine[filesToCombineDataCurrentFileIndex]) {
      mediaObjectCombinationRequestClone.filesToCombine.splice(filesToCombineDataCurrentFileIndex, 1);
      mediaObjectCombinationRequestUpdateAction(mediaObjectCombinationRequest.id, mediaObjectCombinationRequestClone);
    }
  }, [hasFurtherDocument]);

  const handleNextStepClick = async () => {
    setIsLoading(true);
    const finishedStep = getFinishedStep(steps);
    if (hasFurtherDocument && currentStep < FURTHER_DOCUMENT_LIMIT) {
      //save description
      let mediaObjectCombinationRequestClone = structuredClone(mediaObjectCombinationRequest);
      _.set(mediaObjectCombinationRequestClone.filesToCombine, [filesToCombineDataCurrentFileIndex, "description"], description);

      await mediaObjectCombinationRequestUpdateAction(mediaObjectCombinationRequest.id, mediaObjectCombinationRequestClone); //id und data

      setIsLoading(false);

      if (currentStep + 1 === finishedStep) {
        //increase the general number of steps if the user presses next and the next step would be the note step
        setSteps((previousSteps: number) => previousSteps + 1);
      }
      setCurrentStep((previousStep: number) => previousStep + 1);
      return;
    }

    await createMutation.mutateAsync({
      uri: "/messages",
      data: messageCreateData!,
    });

    await queryClient.invalidateQueries({
      queryKey: queryKeys.collection("/conversations"),
    });
    setIsLoading(false);
    setCurrentStep(finishedStep);
  };

  const hasDocument = _.get(mediaObjectCombinationRequest.filesToCombine, `[${filesToCombineDataCurrentFileIndex}].files`, []).length > 0;

  return (
    <ProductBox variant={"clean"} headline={headline}>
      <Grid container>
        <Grid size={12}>
          <Typography sx={{ margin: "1rem 0" }}>{subHeadline}</Typography>
        </Grid>
        <Grid size={12}>
          <Pad
            labelText={"Ja"}
            onChange={() => setHasFurtherDocument(true)}
            type={"radio"}
            active={hasFurtherDocument}
            error={false}
            name={"yes"}
            labelIcon={<CheckIcon />}
            labelIconActive={<CheckIcon active />}
            fullWidth
          />
        </Grid>
        <Grid size={12}>
          <Pad
            labelText={"Nein"}
            onChange={() => setHasFurtherDocument(false)}
            type={"radio"}
            active={!hasFurtherDocument}
            error={false}
            name={"no"}
            labelIcon={<CrossIcon />}
            labelIconActive={<CrossIcon active />}
            fullWidth
          />
        </Grid>
        {hasFurtherDocument && (
          <Grid size={12} sx={{ marginTop: "1rem" }}>
            <UploadBox
              product={product}
              currentFileIndex={filesToCombineDataCurrentFileIndex}
              description={description}
              setDescription={setDescription}
              updateAction={mediaObjectCombinationRequestUpdateAction}
              mediaObjectCombinationRequest={mediaObjectCombinationRequest}
            />
          </Grid>
        )}
        <Grid
          size={{
            xs: 12,
            md: 6,
          }}
          sx={{
            marginTop: { xs: "1rem", md: "2rem" },
          }}
        >
          <Stack direction={"row"} sx={{ justifyContent: "flex-start" }}>
            <Button
              sx={{ display: "inline-block", width: { xs: "100%", md: "auto" }, textAlign: "center" }}
              onClick={() => setCurrentStep((currentStep: number) => currentStep - 1)}
            >
              Zurück
            </Button>
          </Stack>
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 6,
          }}
          sx={{
            marginTop: { xs: "1rem", md: "2rem" },
          }}
        >
          <Stack direction={"row"} sx={{ justifyContent: "flex-end" }}>
            <Button
              sx={{ display: "inline-block", width: { xs: "100%", md: "auto" }, textAlign: "center" }}
              disabled={hasFurtherDocument && (!hasDocument || description.length < 5)}
              variant={"contained"}
              onClick={handleNextStepClick}
            >
              Weiter
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </ProductBox>
  );
}
